import './App.css';
import { Routes, Route, BrowserRouter } from "react-router-dom";
import { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { HelmetProvider } from 'react-helmet-async';
import { fetchLeaderDetailData, fetchMainData, fetchQualificationData, fetchSolutionTypeData, urlUpdateHandle } from './redux/actions/micrositeAction';
import Header from './components/commonSection/Header';
import PageNotFound from './components/pages/PageNotFound';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import useMicroSiteEncrypt from './components/CustomHook/useMicroSiteEncrypt';
import { Toaster } from 'react-hot-toast';
import MainPageContainer from './components/pages/MainPageContainer';
import CategoryContainer from './components/pages/category/CategoryContainer';
import Footer from './components/commonSection/Footer';
import LoadingSpinner from './components/commonSection/LoadingSpinner';
import { isMobile } from 'react-device-detect';
import useContentLoaded from './components/CustomHook/useContentLoaded';

function App() {
  const theme_colors = useSelector((state) => state.microsite?.theme_colors);
  const isLoading = useSelector((state) => state.microsite?.isLoading);
  const home_data = useSelector((state) => state.microsite?.home_data);
  const siteColors = {
      primary: theme_colors?.primary,  // Blue color
      secondary: theme_colors?.secondary,  // Orange color
      textMuted: "#00000070"  // Semi-transparent black
  };
  const dispatch = useDispatch();
  const { microSiteEncrypt, token, error } = useMicroSiteEncrypt();
  const setUrl = "/website";
  const isContentLoaded = useContentLoaded([home_data]);
  
  // Function to preload images with better error handling
  const preloadImages = useCallback((imageUrls) => {
    const promises = imageUrls.map((url) => {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(url);
        img.onerror = () => reject(url);
        img.src = url;
      });
    });
    return Promise.all(promises); // Use Promise.all instead of allSettled to ensure all images load
  }, []);
  
  const captcha_key =
    window.location.origin.includes("spdevmfp") ||
    window.location.origin.includes("localhost")
      ? "6LfunNgbAAAAAEM0X3hcwSBYKJwIqHmmInfz0uFK"
      : window.location.origin.includes("sppreprod") ||
        window.location.origin.includes("sptestmfp")
      ? "6Ldeg6YaAAAAAAZqU7AQ_j-lG4LcnwP4jfNFiCJJ"
      : "6Lf9fqoaAAAAAH8JhnTg9gLI3KsYKmR2tdCRVg8i";
  
  useEffect(() => {
    let payload 
    payload = window?.location?.href || "";    
    dispatch(urlUpdateHandle(payload));
    
    // Set loading to true immediately when component mounts
    dispatch({ type: "TOGGLE_LOADING", payload: true });
    
    const initializeApp = async () => {
      try {
        // Wait for all data to be loaded
        const [homeResult, qualificationResult, solutionResult] = await Promise.allSettled([
          generateHomeToken(),
          generateQualificationToken(),
          generateSolutionTypeToken()
        ]);
        
        // Check if main data was loaded successfully
        if (homeResult.status === 'fulfilled' && homeResult.value) {
          const result = homeResult.value;
          const imageUrls = [];
          
          // Add profile image
          if (result.microsite?.profile_image) {
            imageUrls.push(result.microsite.profile_image);
          }
          
          // Add company logo
          if (result.microsite?.company_logo) {
            imageUrls.push(result.microsite.company_logo);
          }
          
          // Add other images from the response
          if (result.achivements?.data) {
            result.achivements.data.forEach(item => {
              if (item.image) imageUrls.push(item.image);
            });
          }
          
          if (result.testimonials?.data) {
            result.testimonials.data.forEach(item => {
              if (item.image) imageUrls.push(item.image);
            });
          }
          
          // Preload images and then hide loading
          if (imageUrls.length > 0) {
            try {
              await preloadImages(imageUrls);
              console.log('All images preloaded successfully');
            } catch (error) {
              console.log('Some images failed to preload:', error);
              // Even if some images fail, continue with loading
            }
          }
          
          // Check if content is loaded immediately after images are preloaded
          const checkContentLoaded = () => {
            if (isContentLoaded) {
              dispatch({ type: "TOGGLE_LOADING", payload: false });
            } else {
              // If content not ready, wait a bit more
              setTimeout(checkContentLoaded, 100);
            }
          };
          checkContentLoaded();
        } else {
          // If main data failed, check content immediately
          const checkContentLoaded = () => {
            if (isContentLoaded) {
              dispatch({ type: "TOGGLE_LOADING", payload: false });
            } else {
              setTimeout(checkContentLoaded, 100);
            }
          };
          checkContentLoaded();
        }
      } catch (error) {
        console.error('Error during app initialization:', error);
        // Hide loading even if there's an error
        const checkContentLoaded = () => {
          if (isContentLoaded) {
            dispatch({ type: "TOGGLE_LOADING", payload: false });
          } else {
            setTimeout(checkContentLoaded, 100);
          }
        };
        checkContentLoaded();
      }
    };
    
    const generateHomeToken = async () => {
      const tokenHome = await microSiteEncrypt();
      if (tokenHome && tokenHome !== "") {
        return await dispatch(fetchMainData(tokenHome, payload));
      }
      return null;
    };
    
    const generateQualificationToken = async () => {
      const tokenQ = await microSiteEncrypt();
      if (tokenQ && tokenQ !== "") {
        return await dispatch(fetchQualificationData(tokenQ));
      }
      return null;
    };
    
    const generateSolutionTypeToken = async () => {
      const tokenStype = await microSiteEncrypt();
      if (tokenStype && tokenStype !== "") {
        return await dispatch(fetchSolutionTypeData(tokenStype));
      }
      return null;
    };
    
    // Start the initialization process
    initializeApp();
  }, []);
  
  useEffect(() => {
    
    // if(window.location.origin.includes("sptestmfp.com") || window.location.origin.includes("abcstellarpartner.com")) return;

    if (window.location.origin.includes("localhost")) {
    return;
    }
    
    const script = document.createElement('script');

    script.src = window.location.origin + "/chatbot/js/loadContent.js"; // URL of the script you want to load
    script.async = true; // Load the script asynchronously
    script.onload = () => {
      console.log('Script loaded successfully');
    };
    
    setTimeout(() => {
      document.body.appendChild(script);  
    }, 3000);

    // Cleanup function to remove the script when the component unmounts
    return () => {
      document.body.removeChild(script);
    };
  }, []);
  
  return (
    <HelmetProvider>
      <div className="App">
        <BrowserRouter basename={setUrl}>
          <GoogleReCaptchaProvider reCaptchaKey={captcha_key}>
          <Toaster position="bottom-center" reverseOrder={false} />
          <Header/>
          <Routes>
            <Route path="/" >
              <Route path='category' index={false} element={<CategoryContainer authToken={"authToken"} pageName="product" subPageName="conatiner"/>} />
              <Route index={true} element={<MainPageContainer authToken={"authToken"} pageName="product" subPageName="conatiner"/>} />
            </Route>
            <Route path="*" element={<PageNotFound />} />
          </Routes>
          {/* {isMobile ? <Footer colors={siteColors} />:""} */}
          </GoogleReCaptchaProvider>
        </BrowserRouter>
        <div id='chatbot' ></div>
      </div>
    </HelmetProvider>
  );
}

export default App;
